// Copyright (c) Microsoft. All rights reserved.

import { useMsal } from '@azure/msal-react';
import { Button, makeStyles, Title3, Image, shorthands } from '@fluentui/react-components';
import React from 'react';
import { getErrorDetails } from '../utils/TextUtils';

const useClasses = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
    },
    title: {
        fontSize: '28px',
        fontWeight: 'bold',
        color: '#323130',
        ...shorthands.margin('0', '0', '24px', '0'),
    },
    loginBox: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        ...shorthands.padding('30px', '25px'),
        ...shorthands.margin('10px', '0'),
        ...shorthands.borderRadius('12px'),
        ...shorthands.border('1px', 'solid', '#E1DFDD'),
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
        backgroundColor: '#FFFFFF',
        width: '340px',
        position: 'relative',
        overflow: 'hidden',
    },
    backgroundPattern: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage:
            'linear-gradient(135deg, #f0f0f0 25%, transparent 25%, transparent 50%, #f0f0f0 50%, #f0f0f0 75%, transparent 75%, transparent)',
        backgroundSize: '40px 40px',
        opacity: 0.4,
        zIndex: 0,
    },
    contentWrapper: {
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
    },
    loginButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...shorthands.gap('10px'),
        ...shorthands.padding('12px', '24px'),
        ...shorthands.margin('20px', '0', '0', '0'),
        fontWeight: '600',
        backgroundColor: '#0078D4',
        color: '#FFFFFF',
        ...shorthands.borderRadius('6px'),
        boxShadow: '0 2px 8px rgba(0, 120, 212, 0.3)',
        width: '100%',
        transitionProperty: 'all',
        transitionDuration: '0.2s',
        transitionTimingFunction: 'ease-in-out',
        ':hover': {
            backgroundColor: '#106EBE',
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(0, 120, 212, 0.4)',
        },
    },
    kaiLogo: {
        width: '80px',
        height: '80px',
        ...shorthands.margin('0', '0', '20px', '0'),
        filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))',
    },
    welcomeText: {
        fontSize: '20px',
        fontWeight: '600',
        color: '#323130',
        ...shorthands.margin('0', '0', '12px', '0'),
        textAlign: 'center',
    },
    descriptionText: {
        fontSize: '14px',
        color: '#605E5C',
        textAlign: 'center',
        ...shorthands.margin('0', '0', '15px', '0'),
    },
});

export const Login: React.FC = () => {
    const { instance } = useMsal();
    // const classes = useSharedClasses();
    const loginClasses = useClasses();

    return (
        <div className={loginClasses.container}>
            <Title3 className={loginClasses.title}>KAI&apos;ye Hoşgeldiniz!</Title3>

            <div className={loginClasses.loginBox}>
                <div className={loginClasses.backgroundPattern} />

                <div className={loginClasses.contentWrapper}>
                    <Image
                        src={`${process.env.PUBLIC_URL}/favicon/favicon-96x96.png`}
                        className={loginClasses.kaiLogo}
                        alt="KAI Logo"
                    />

                    <Button
                        className={loginClasses.loginButton}
                        onClick={() => {
                            instance.loginRedirect().catch((e: unknown) => {
                                alert(`Error signing in: ${getErrorDetails(e)}`);
                            });
                        }}
                        data-testid="signinButton"
                    >
                        Kurumsal Hesap İle Oturum Açınız
                    </Button>
                </div>
            </div>
        </div>
    );
};
